import React, { useEffect, useState } from "react";
import { Wallet, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";

const TYPE_LABEL = {
  CASHBACK_FUND: "Cashback fund (60%)", REWARD_FUND: "Reward/Lucky-draw fund (40%)",
  CASHBACK_DEBIT: "Cashback paid", REVERSAL: "Cashback reversal",
  SUBSCRIPTION_FUNDING: "Funding record",
};

export default function Budget() {
  const [d, setD] = useState(null);
  const load = () => api.get("/admin/budget/summary").then((r) => setD(r.data)).catch(() => toast.error("Could not load budget"));
  useEffect(() => { load(); }, []);
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-budget">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Wallet className="h-5 w-5 text-emerald-400" /><h1 className="font-heading text-2xl font-bold">Central campaign budget</h1></div>
        <button data-testid="budget-refresh" onClick={load} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-4 py-2 text-sm hover:border-emerald-500/50"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="ledger-card rounded-2xl border-emerald-500/30 p-5" data-testid="budget-cashback-fund">
          <p className="font-mono text-[11px] uppercase tracking-widest text-emerald-400/80">Cashback fund · 60%</p>
          <p className="mt-2 font-heading text-3xl font-extrabold text-emerald-400">{d.cashback_fund?.display ?? d.remaining.display}</p>
          <p className="mt-1 text-xs text-slate-500">Funds ₹20 / ₹50 / ₹100 instant cashback only {d.cashback_reconciled ? "· ✓ reconciled" : "· ⚠ mismatch"}</p>
        </div>
        <div className="ledger-card rounded-2xl border-amber-500/30 p-5" data-testid="budget-reward-fund">
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber-400/80">Reward / Lucky-draw fund · 40%</p>
          <p className="mt-2 font-heading text-3xl font-extrabold text-amber-400">{d.reward_fund?.display ?? "₹0.00"}</p>
          <p className="mt-1 text-xs text-slate-500">Funds Lucky Draw prizes only {d.reward_reconciled ? "· ✓ reconciled" : "· ⚠ mismatch"}</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card label="Total funded" value={d.funded.display} accent testid="budget-funded" />
        <Card label="Cashback used" value={d.used.display} testid="budget-used" />
        <Card label="Reversed" value={d.reversed.display} testid="budget-reversed" />
        <Card label="Remaining (both funds)" value={d.remaining.display} accent testid="budget-remaining" />
      </div>

      <div className="ledger-card rounded-2xl p-5" data-testid="budget-reconcile">
        <div className="flex flex-wrap items-center gap-6 text-sm">
          <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Ledger vs guard</span>
          <span>Ledger: <span className="font-mono text-emerald-400">{d.ledger_balance.display}</span></span>
          <span>Guard: <span className="font-mono text-emerald-400">{d.remaining.display}</span></span>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${d.reconciled ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>{d.reconciled ? "RECONCILED" : "MISMATCH"}</span>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="ledger-card rounded-2xl p-5 lg:col-span-1">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Movement breakdown</p>
          <div className="mt-3 space-y-3">
            {d.breakdown.map((b) => (
              <div key={b.type} className="flex items-center justify-between">
                <span className="text-sm text-slate-300">{TYPE_LABEL[b.type] || b.type}<span className="ml-1 text-xs text-slate-600">×{b.count}</span></span>
                <span className={`font-mono text-sm font-semibold ${b.total_paise < 0 ? "text-rose-400" : "text-emerald-400"}`}>{b.total_display}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="ledger-card overflow-hidden rounded-2xl lg:col-span-2">
          <p className="p-4 font-mono text-[11px] uppercase tracking-widest text-slate-500">Recent movements</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="budget-movements-table">
              <thead className="bg-slate-900/50 font-mono text-xs uppercase tracking-wider text-slate-500"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Type</th><th className="p-3 text-right">Amount</th><th className="p-3 text-left">Ref</th></tr></thead>
              <tbody>
                {d.movements.map((m) => (
                  <tr key={m.entry_id} className="border-t border-slate-800/60">
                    <td className="p-3 text-slate-400">{new Date(m.created_at).toLocaleString("en-IN")}</td>
                    <td className="p-3 font-mono text-xs">{m.type}</td>
                    <td className={`p-3 text-right font-mono ${m.amount_paise < 0 ? "text-rose-400" : "text-emerald-400"}`}>{m.amount_display}</td>
                    <td className="p-3 font-mono text-xs text-slate-500">{m.ref || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card({ label, value, accent, testid }) {
  return (
    <div className="ledger-card rounded-2xl p-5" data-testid={testid}>
      <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className={`mt-2 font-heading text-2xl font-extrabold ${accent ? "text-emerald-400" : "text-slate-100"}`}>{value}</p>
    </div>
  );
}
